import _ from 'lodash'
import { removeInitializing } from '../../components/localStorageUtils'
import { addByIdAndVersion } from '../ContextUtils'
import { leaderContextHack } from '../LeaderContext/LeaderContext';
import LocalForageHelper from '../../utils/LocalForageHelper';
import { MARKET_PRESENCES_CONTEXT_NAMESPACE } from './MarketPresencesContext';
import { queuePersistenceWrite } from '../../api/crossTabFreshness';

const INITIALIZE_STATE = 'INITIALIZE_STATE';
const ADD_MARKET_PRESENCE = 'ADD_MARKET_PRESENCE';
const ADD_MARKET_PRESENCES = 'ADD_MARKET_PRESENCES';
const UPDATE_FROM_VERSIONS = 'UPDATE_FROM_VERSIONS';
const REMOVE_MARKETS_PRESENCE = 'REMOVE_MARKETS_PRESENCE';
const REMOVE_INVESTIBLE_INVESTMENTS = 'REMOVE_INVESTIBLE_INVESTMENTS'
const PATCH_INVESTMENT = 'PATCH_INVESTMENT';
const BANNED_MARKETS = 'BANNED_MARKETS';

/** Messages you can send the reducer **/

export function initializeState(newState) {
  return {
    type: INITIALIZE_STATE,
    newState,
  };
}

export function patchInvestment(investmentPatch, allowMultiVote) {
  return {
    type: PATCH_INVESTMENT,
    investmentPatch,
    allowMultiVote
  };
}

export function removeInvestments(marketId, investibleId) {
  return {
    type: REMOVE_INVESTIBLE_INVESTMENTS,
    marketId,
    investibleId
  };
}

export function processBanned(bannedList) {
  return {
    type: BANNED_MARKETS,
    bannedList
  }
}

export function addMarketPresence(marketId, user) {
  return {
    type: ADD_MARKET_PRESENCE,
    marketId,
    user,
  };
}

export function addMarketPresences(marketId, users) {
  return {
    type: ADD_MARKET_PRESENCES,
    marketId,
    users
  };
}

export function versionsUpdateMarketPresences(userDetails) {
  return {
    type: UPDATE_FROM_VERSIONS,
    userDetails
  };
}

export function removeMarketsPresence(marketIds) {
  return {
    type: REMOVE_MARKETS_PRESENCE,
    marketIds,
  };
}

/** Functions that update the state **/

function doPatchInvestment(state, action) {
  const { investmentPatch, allowMultiVote } = action;
  const oldMarketUsers = state[investmentPatch.market_id] || [];
  const oldPresence = oldMarketUsers.find((oldUser) => oldUser.id === investmentPatch.user_id);
  if (_.isEmpty(oldPresence)) {
    // I don't even have a presence, I can't do anything
    return state;
  }
  const investments = oldPresence.investments || [];
  const newInvestments = allowMultiVote ?  _.unionBy([investmentPatch], investments, 'investible_id')
    : [investmentPatch];
  const newPresence = {
    ...oldPresence,
    investments: newInvestments
  };
  const newMarketUsers = _.unionBy([newPresence], oldMarketUsers, 'id');
  return {
    ...state,
    [investmentPatch.market_id]: newMarketUsers,
  };
}



function doAddMarketPresence(state, action) {
  const { marketId, user } = action;
  const oldUsers = state[marketId] || [];
  const newUsers = _.unionBy([user], oldUsers, 'id');
  return {
    ...state,
    [marketId]: newUsers,
  };
}

function doAddMarketPresences(state, action) {
  const { marketId, users } = action;
  const oldUsers = state[marketId] || [];
  const newUsers = _.unionBy(users, oldUsers, 'id');
  return {
    ...state,
    [marketId]: newUsers,
  };
}

function doUpdateMarketPresences(state, action) {
  const { userDetails } = action;
  const newState = {...state};
  Object.keys(userDetails).forEach((marketId) => {
    const incomingUsers = userDetails[marketId] || [];
    const existingUsers = newState[marketId] || [];
    const existingUsersById = _.keyBy(existingUsers, 'id');
    // Carry forward any investments that existed on the prior user but are
    // missing from the incoming user so version replacement does not drop them
    const mergedIncomingUsers = incomingUsers.map((incomingUser) => {
      if (!incomingUser) {
        return incomingUser;
      }
      const existingUser = existingUsersById[incomingUser.id];
      if (_.isEmpty(existingUser?.investments)) {
        return incomingUser;
      }
      const incomingInvestments = incomingUser.investments || [];
      const mergedInvestments = _.unionBy(incomingInvestments, existingUser.investments,
        'investible_id');
      return {
        ...incomingUser,
        investments: mergedInvestments,
      };
    });
    // Avoid clobbering presences that were quick added
    newState[marketId] = addByIdAndVersion(mergedIncomingUsers, newState[marketId]);
  });
  return removeInitializing(newState);
}

function doRemoveInvestibleInvestments(state, action) {
  const { marketId, investibleId } = action;
  const oldUsers = state[marketId] || [];
  const transformedUsers = oldUsers.map((user) => {
    const newInvestments = user.investments?.filter((investment) =>
      investment.investible_id !== investibleId);
    return {
      ...user,
      investments: newInvestments
    };
  });
  return {
    ...state,
    [marketId]: transformedUsers,
  };
}

// This can only come from the network
function doProcessBanned(state, action) {
  const { bannedList } = action;
  if (_.isEmpty(bannedList)) {
    // If he has been unbanned then this is a no op because normal market syncing will change his presence
    return state;
  }
  let newState = state;
  bannedList.forEach((marketId) => {
    const oldMarketUsers = state[marketId] || [];
    const myUser = oldMarketUsers.find((user) => user.current_user) || {};
    if (!myUser.market_banned) {
      const newPresence = {
        ...myUser,
        market_banned: true,
      };
      const newMarketUsers = _.unionBy([newPresence], oldMarketUsers, 'id');
      newState = doUpdateMarketPresences(newState, {userDetails: {[marketId]: newMarketUsers}})
    }
  });
  return newState;
}

function doRemoveMarketsPresence(state, action) {
  const { marketIds } = action;
  return _.omit(state, marketIds);
}

function computeNewState(state, action) {
  switch (action.type) {
    case INITIALIZE_STATE:
      return action.newState;
    case ADD_MARKET_PRESENCE:
      return doAddMarketPresence(state, action);
    case ADD_MARKET_PRESENCES:
      return doAddMarketPresences(state, action);
    case UPDATE_FROM_VERSIONS:
      return doUpdateMarketPresences(state, action);
    case REMOVE_MARKETS_PRESENCE:
      return doRemoveMarketsPresence(state, action);
    case PATCH_INVESTMENT:
      return doPatchInvestment(state, action);
    case BANNED_MARKETS:
      return doProcessBanned(state, action);
    case REMOVE_INVESTIBLE_INVESTMENTS:
      return doRemoveInvestibleInvestments(state, action);
    default:
      return state;
  }
}

function reducer(state, action) {
  const newState = computeNewState(state, action);
  if (action.type !== INITIALIZE_STATE && newState !== state) {
    const { isLeader } = leaderContextHack;
    if (isLeader) {
      const lfh = new LocalForageHelper(MARKET_PRESENCES_CONTEXT_NAMESPACE);
      queuePersistenceWrite('presences', () => {
        return lfh.setState(newState).then(() => {
          console.info('Updated presence context storage.');
        });
      });
    }
  }
  return newState;
}

export default reducer;
